import { useCallback, useMemo } from 'react';
import useAuth from './useAuth';

/**
 * The word this organisation's industry uses for a workplace.
 *
 * "Job Site" is construction's word, and it is written into navigation,
 * headings, buttons and empty states throughout the portal. A cafe has Venues,
 * a warehouse has Warehouses and depots, a hospital has Service locations.
 *
 * The words come from the API on /v1/users/me (organisations.workplaceTermsFor)
 * rather than being derived here, because they belong to the business type and
 * the backend already has to know them for emails and notifications. One source
 * avoids the two drifting.
 *
 * The default is today's wording, not Construction's. Anyone whose organisation
 * has no business type — or who is on a screen before the user has loaded — sees
 * exactly what they see now. A fallback that guessed "Site" would flicker the
 * navigation on every page load between the default and the real value.
 */
export type WorkplaceTerms = {
  /** Singular, title case: "Job Site", "Venue". */
  noun: string;
  /** Plural, title case: "Job Sites", "Warehouses and depots". */
  nounPlural: string;
  /** Singular, lower case, for mid-sentence use: "add a venue". */
  nounLower: string;
  /** Plural, lower case: "3 venues". */
  nounPluralLower: string;
  /** Navigation titles this industry does not show. Empty for construction. */
  hiddenModules: string[];
  /**
   * Navigation titles this industry words differently, keyed by the title as
   * written: `{ 'On site now': 'In the venue now' }`.
   *
   * The third case, between renaming the workplace and hiding a module: an item
   * every industry needs, under a name only construction uses. Not derivable
   * from the noun — "On Venue now" is not English, and the preposition moves
   * with the industry — so it is looked up rather than computed. Empty for
   * construction, which keeps every title as written.
   */
  navLabels: Record<string, string>;
};

/**
 * Neutral, industry-free wording for screens that span every customer at once.
 *
 * Every other surface takes its noun from the VIEWER's industry, which is right
 * when the viewer owns what they are looking at. An admin does not: their list
 * holds a building site, a cafe and a warehouse in the same table, so reading
 * the viewer's own industry answers the wrong question and — since TWC's own
 * account is construction — silently reprinted "Job Sites" over everybody's
 * data. There is no industry whose word is correct here, so the product's own
 * neutral term is the only honest one.
 */
export const NEUTRAL_WORKPLACE_TERMS: WorkplaceTerms = {
  noun: 'Workplace',
  nounPlural: 'Workplaces',
  nounLower: 'workplace',
  nounPluralLower: 'workplaces',
  hiddenModules: [],
  navLabels: {},
};

export const DEFAULT_WORKPLACE_TERMS: WorkplaceTerms = {
  noun: 'Job Site',
  nounPlural: 'Job Sites',
  nounLower: 'job site',
  nounPluralLower: 'job sites',
  hiddenModules: [],
  navLabels: {},
};

/**
 * `navLabel('On site now')` — the industry's wording for a fixed label,
 * falling back to the label as written.
 *
 * The same lookup the sidebar does, for the headings and buttons that repeat a
 * navigation item's name outside the navigation. Passing the construction
 * wording as the key rather than an invented id keeps one identity for the
 * item: the string the portal already uses everywhere else.
 */
export function useNavLabel(): (title: string) => string {
  const { navLabels } = useWorkplaceTerm();
  // Memoised so the returned function is a stable identity. Callers put it in
  // dependency arrays, and a fresh closure every render would re-fire their
  // effects forever.
  return useCallback((title: string) => navLabels[title] || title, [navLabels]);
}

// Shared empty values.
//
// `[]` and `{}` written inline would be a NEW reference on every render, and
// these are the values Construction gets — its hiddenModules and navLabels are
// both NULL, by design, because it renames nothing. That made the hook return a
// fresh object on every render for exactly the users who need no renaming at
// all, and any effect depending on it looped until React gave up with "Maximum
// update depth exceeded". Frozen module constants instead, so the identity is
// as stable as the value.
const NO_MODULES: string[] = [];
const NO_LABELS: Record<string, string> = {};

export default function useWorkplaceTerm(): WorkplaceTerms {
  const { user } = useAuth();
  const workplace = (user as { workplace?: unknown } | null)
    ?.workplace as
    | {
        noun?: string;
        nounPlural?: string;
        hiddenModules?: string[];
        navLabels?: Record<string, string> | null;
      }
    | undefined;

  const noun = workplace?.noun;
  const nounPlural = workplace?.nounPlural;
  const hiddenModules = workplace?.hiddenModules;
  const navLabels = workplace?.navLabels;

  // Memoised on the RAW fields rather than the derived object. The derived one
  // is rebuilt here and would otherwise be a new reference every render — the
  // thing that made this hook unusable in a dependency array.
  //
  // Called unconditionally, before any early return: a hook that runs on some
  // renders and not others breaks the rules it is bound by.
  return useMemo(() => {
    if (!noun || !nounPlural) return DEFAULT_WORKPLACE_TERMS;
    return {
      noun,
      nounPlural,
      // Lower-cased here rather than stored twice. Safe for the seeded values,
      // which are ordinary words; a type whose name contained an acronym would
      // need the API to carry the cased forms instead.
      nounLower: noun.toLowerCase(),
      nounPluralLower: nounPlural.toLowerCase(),
      hiddenModules: Array.isArray(hiddenModules) ? hiddenModules : NO_MODULES,
      // Null (Construction, no industry, or an unreadable row) becomes an empty
      // map, which renames nothing — the same "leave it as written" answer.
      navLabels: navLabels && typeof navLabels === 'object' ? navLabels : NO_LABELS,
    };
  }, [noun, nounPlural, hiddenModules, navLabels]);
}
